import React from 'react';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import PlaybookCallout from '../../../components/playbooks/PlaybookCallout';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';
import ToolCallout from '../../../components/playbooks/ToolCallout';
import { 
  MagnifyingGlassIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';

const ResettingBehavioursStage2 = () => {
  return (
    <div className="space-y-8">
      <PlaybookSection
        title="Stage 2: Dig deeper, what's the source of the anti-behaviours?"
        icon={<MagnifyingGlassIcon className="h-7 w-7 text-primary-light" />}
        heading="Understand root causes and plan interventions"
      >
        <ExpandableCallout
          type="info"
          title="What do I need to DO in this stage?"
          defaultOpen={true}
        >
          <div className="space-y-4">
            <p className="text-gray-300">You need to do 3 things:</p>
            <ol className="space-y-4">
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">1.</span>
                <span className="text-gray-300">Speak to folks, listen and get a read on root cause</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">2.</span>
                <span className="text-gray-300">Zero in on the kind of intervention that's needed</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">3.</span>
                <span className="text-gray-300">Co-create commitment</span>
              </li>
            </ol>
          </div>
        </ExpandableCallout>

        <PlaybookCard>
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
            <ChatBubbleLeftRightIcon className="h-6 w-6 text-primary-light mr-3" />
            Listen and get a read on the root cause
          </h2>
          <div className="space-y-6">
            <p className="text-gray-300">
              So you've openly acknowledged the problem. You've done the hardest bit - good job! 🎉
            </p>
            <p className="text-gray-300">
              You might have a few people DM you after the town hall, or a few folks who were active on sli.do or similar that you proactively follow up with 1-2-1.
            </p>
            <p className="text-gray-300">
              It depends on the size of the problem and where the frustrations really are.
            </p>
            <p className="text-gray-300">
              Regardless, the job now is to speak to folks, listen and understand what's really going on.
            </p>

            <div>
              <h3 className="text-lg font-semibold text-white mb-2">You are looking to get a read on:</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  <span className="text-gray-300">Do we have the 'right' behaviours documented?</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  <span className="text-gray-300">Is there something more systemic going on? Classic example: the way sales and marketing are incentivised is actually what's driving the anti-behaviours.</span>
                </li>
              </ul>
            </div>

            <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700/50">
              <div className="flex items-start">
                <LightBulbIcon className="h-6 w-6 text-primary-light mr-3 mt-1" />
                <div>
                  <p className="text-gray-300">
                    If you are physical, sometimes taking folks offsite for dinner/drinks can help change the environment and encourage folks to open up about talking about what's really going on.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Questions to ask:</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  <span className="text-gray-300">What do you think is causing…?</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  <span className="text-gray-300">What needs to happen to change the way we…?</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  <span className="text-gray-300">How do you wish we…?</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  <span className="text-gray-300">What's stopping us working in a way that…?</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  <span className="text-gray-300">How might we…?</span>
                </li>
              </ul>
            </div>

            <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700/50">
              <div className="flex items-start">
                <LightBulbIcon className="h-6 w-6 text-primary-light mr-3 mt-1" />
                <div>
                  <p className="text-gray-300">
                    Tip: try and swap "why" for "what's the reason you…" - some folks find 'why' a bit judgemental and can go on the defensive. Not what you want.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </PlaybookCard>

        <PlaybookCard>
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
            <ChartBarIcon className="h-6 w-6 text-primary-light mr-3" />
            Zero in on what intervention is needed
          </h2>
          <p className="text-gray-300">
              Collect your insights from speaking to people 1-2-1 or if you hosted an AMA or took folks out. Look for patterns and bucket your findings.
            </p>
          <PlaybookImage
            src="https://auth.openorg.fyi/storage/v1/object/public/resources/resetting-behaviour/chart-example.png"
            alt="Presenting findings using graphy.app"
            caption="Presenting findings using graphy.app"
          />
          <div className="space-y-6">
            

            <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700/50">
              <h4 className="text-white font-semibold mb-2">Example Findings:</h4>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  <span className="text-gray-300">VP conflicts affecting team dynamics</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  <span className="text-gray-300">Unrealistic OKRs and deadlines</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  <span className="text-gray-300">Individual stress and pressure</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  <span className="text-gray-300">Wellbeing and work-life balance concerns</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Required Actions:</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  <span className="text-gray-300">Speak to the VPs about their perceived conflict</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  <span className="text-gray-300">Address OKRs and deadlines with the senior leadership team</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  <span className="text-gray-300">1-2-1 with individuals about stress/pressure</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  <span className="text-gray-300">Review company approach to wellbeing</span>
                </li>
              </ul>
            </div>
          </div>
        </PlaybookCard>

        <PlaybookCard>
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
            <DocumentTextIcon className="h-6 w-6 text-primary-light mr-3" />
            Communicate what you've learned
          </h2>
          <div className="space-y-6">
            <p className="text-gray-300">
              After agreeing on an action plan with the leadership team, it's important to close the loop with the teams and share transparently what you're doing about it.
            </p>

            <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700/50">
              <h4 className="text-white font-semibold mb-2">Example Communication:</h4>
              <div className="space-y-4">
                <p className="text-gray-300">Folks, thanks for all your feedback following the town hall where I shared some observations about how we work together.</p>
                <p className="text-gray-300">I've learned that there are a couple of things that are driving this.</p>
                <p className="text-gray-300">Here's what we as a leadership are doing about it:</p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-primary-light mr-2">—</span>
                    <span className="text-gray-300">Action: The next OKR planning session will include a confidence dot vote</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-light mr-2">—</span>
                    <span className="text-gray-300">Action: We're overhauling how we incentivise and set goals across functions</span>
                  </li>
                </ul>
                <p className="text-gray-300">Appreciate everyone for helping us collectively improve here.</p>
              </div>
            </div>
          </div>
        </PlaybookCard>

        <PlaybookCard>
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
            <CheckCircleIcon className="h-6 w-6 text-primary-light mr-3" />
            Outcome
          </h2>
          <ul className="space-y-4">
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span className="text-gray-300">You listened and understood what the root cause is</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span className="text-gray-300">You looped in the leadership team to co-create an action plan</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span className="text-gray-300">You communicated transparently how you're solving the problem</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span className="text-gray-300">You earned trust because you followed through aka 'closed the loop'</span>
            </li>
          </ul>
        </PlaybookCard>

        <PlaybookCallout
          badge="Related Guide"
          intro="Struggling with conflict? You might find this playbook helpful:"
          type="playbook"
          resourceSlug="working-through-conflict"
        />
      </PlaybookSection>
    </div>
  );
};

export default ResettingBehavioursStage2; 